import { QueryResult } from '@apollo/client/react';
import QuillDelta from 'quill-delta';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box } from 'reflexbox';

import { useAuthenticatedUser, useLocalize } from '@treams/treams-hooks';
import { TargetIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Empty, InfiniteScroll, Modal, Popover, Spinner, useToasts } from '@treams/treams-ui';

import GoalCard from '../../../../../goals/GoalCard';
import { toChecklistItemInput } from '../../../../../goals/toChecklistItemInput';

function GoalPickerList(props: {
	query: ReturnType<typeof graphql.useIndividualGoalsQuery>;
	selectedGoalID?: string;
	onSelect: (goalID: string) => void;
	emptyTitle: string;
}) {
	const goals = useMemo(
		() =>
			(props.query.data?.user.goalsV2.edges ?? [])
				.map(({ node }) => node)
				.sort((a, b) => b.order - a.order),
		[props.query.data?.user.goalsV2.edges],
	);

	if (props.query.loading && goals.length === 0) {
		return (
			<Box p={4}>
				<Spinner centered />
			</Box>
		);
	}

	if (goals.length === 0) {
		return (
			<Box p={3}>
				<Empty.Card icon={<TargetIcon />} title={props.emptyTitle} small />
			</Box>
		);
	}

	return (
		<Box p={3}>
			{goals.map((goal) => (
				<Box key={goal.id} mb={2}>
					<GoalCard
						small
						owner={false}
						goal={goal as graphql.Goal}
						selected={props.selectedGoalID === goal.id}
						onSelect={() => props.onSelect(goal.id)}
					/>
				</Box>
			))}
			<InfiniteScroll
				query={props.query as QueryResult<graphql.IndividualGoalsQuery>}
				path="user.goalsV2"
				reverse
			/>
		</Box>
	);
}

export default function LearningCourseLinkModal(props: {
	course: graphql.LearningCourseFragment;
	onClose: () => void;
}) {
	const { t } = useTranslation();
	const { language } = useLocalize();
	const history = useHistory();
	const { addToast } = useToasts();
	const viewer = useAuthenticatedUser();

	const [selectedGoalID, setSelectedGoalID] = useState<string>();
	const [saving, setSaving] = useState(false);

	const activeQuery = graphql.useIndividualGoalsQuery({
		variables: {
			id: viewer.id,
			status: graphql.GoalStatus.Active,
			last: 20,
		},
		fetchPolicy: 'network-only',
	});

	const selectedGoal = graphql.useIndividualGoalQuery({
		variables: { id: selectedGoalID! },
		skip: !selectedGoalID,
	});

	const [updateGoal] = graphql.useUpdateIndividualGoalMutation();

	const handleLink = useCallback(async () => {
		const goal = selectedGoal.data?.goal;

		if (saving) {
			return;
		}

		if (!goal) {
			addToast(t('learning.library.course.linkToGoal.error'), {
				appearance: 'error',
				autoDismiss: true,
			});
			return;
		}

		const alreadyLinked = goal.checklistItems.some(
			(item) => item.learningCourseID === props.course.id,
		);

		if (alreadyLinked) {
			addToast(t('learning.library.course.linkToGoal.alreadyLinked'), {
				appearance: 'info',
				autoDismiss: true,
			});
			history.push(`/goals/${goal.id}`);
			return;
		}

		setSaving(true);

		try {
			await updateGoal({
				variables: {
					input: {
						id: goal.id,
						title: goal.title,
						description: goal.description,
						dueDate: goal.dueDate,
						feasibility: goal.feasibility ?? undefined,
						labelID: goal.label?.id,
						visibility: goal.visibility,
						linkedGoalDriverIDs: goal.linkedGoalDrivers.map((driver) => driver.id),
						linkedGoalIDs: goal.linkedGoals.map((linkedGoal) => linkedGoal.id),
						competency: goal.competency
							? {
									competencyID: goal.competency.competency.id,
									competencyLevelIndex: goal.competency.competencyLevelIndex,
								}
							: undefined,
						checklistItems: [
							...goal.checklistItems.map((item) =>
								toChecklistItemInput(item as graphql.GoalChecklistItem),
							),
							{
								title: props.course.title,
								description: [{ languageID: language.id, text: new QuillDelta() }],
								progress: {
									type: graphql.GoalChecklistItemType.Learning,
									value: 0,
								},
								learningCourseID: props.course.id,
							},
						],
					},
				},
			});

			addToast(t('learning.library.course.linkToGoal.success'), {
				appearance: 'success',
				autoDismiss: true,
			});
			history.push(`/goals/${goal.id}`);
		} catch {
			addToast(t('learning.library.course.linkToGoal.error'), {
				appearance: 'error',
				autoDismiss: true,
			});
			setSaving(false);
		}
	}, [
		addToast,
		history,
		language.id,
		props.course.id,
		props.course.title,
		saving,
		selectedGoal.data?.goal,
		t,
		updateGoal,
	]);

	return (
		<Popover center visible zIndex={5} onDismiss={props.onClose}>
			<Modal>
				<Modal.Header
					title={t('learning.library.course.linkToGoal.select.title')}
					onClose={props.onClose}
				/>
				<Modal.Contents p={0}>
					<GoalPickerList
						query={activeQuery}
						selectedGoalID={selectedGoalID}
						onSelect={setSelectedGoalID}
						emptyTitle={t('goals.index.empty.title')}
					/>
				</Modal.Contents>
				<Modal.FormFooter
					onCancel={props.onClose}
					onSave={handleLink}
					saveTitle={t('learning.library.course.linkToGoal.select.confirm')}
					disabled={!selectedGoalID || saving || selectedGoal.loading}
				/>
			</Modal>
		</Popover>
	);
}
